'use client';

import { useState, useEffect } from 'react';
import Modal from '@/components/ui/Modal';
import { PRIVACY_NOTICES, type PrivacyNoticeAudience, type PrivacyNoticeBlock } from '@/content/legal';

interface PrivacyNoticeModalProps {
  open: boolean;
  onClose: () => void;
  initialAudience?: PrivacyNoticeAudience;
}

export default function PrivacyNoticeModal({
  open,
  onClose,
  initialAudience = 'student',
}: PrivacyNoticeModalProps) {
  const [audience, setAudience] = useState<PrivacyNoticeAudience>(initialAudience);

  // When the host re-opens the modal with a different default (e.g. student
  // login page vs teacher login page), reset to that default. Without this,
  // the previous selection leaks across opens.
  useEffect(() => {
    if (open) setAudience(initialAudience);
  }, [open, initialAudience]);

  const notice = PRIVACY_NOTICES[audience];

  return (
    <Modal open={open} onClose={onClose} title="Privacy Notice" wide>
      <div className="px-[30px] py-5">
        {/* Audience toggle — segmented buttons with aria-pressed (deliberately
            not the ARIA tabs pattern, which would also need a tabpanel,
            aria-controls, roving tabindex, and arrow-key handling). */}
        <div
          role="group"
          aria-label="Privacy Notice audience"
          className="inline-flex rounded-full border-2 border-purple-200 p-1 bg-white mb-6"
        >
          {(Object.keys(PRIVACY_NOTICES) as PrivacyNoticeAudience[]).map((key) => {
            const active = key === audience;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={active}
                onClick={() => setAudience(key)}
                className={`min-h-[44px] rounded-full px-4 py-2 text-sm font-baloo-2 font-semibold transition-colors ${
                  active
                    ? 'bg-brand-purple text-white'
                    : 'bg-transparent text-grayscale-500 hover:text-grayscale-600'
                }`}
              >
                {PRIVACY_NOTICES[key].label}
              </button>
            );
          })}
        </div>

        {/* Notice body */}
        <article>
          {notice.title && (
            <h2 className="font-sans text-2xl font-bold text-brand-dark mb-3">
              {notice.title}
            </h2>
          )}

          {notice.intro.map((p, i) => (
            <p
              key={`intro-${i}`}
              className="font-sans text-base leading-relaxed text-grayscale-600 mb-3"
            >
              {p}
            </p>
          ))}

          {notice.blocks.map((block, i) => (
            <BlockRenderer key={i} block={block} />
          ))}

          <footer className="mt-8 pt-4 border-t border-grayscale-200 text-sm font-sans text-grayscale-500">
            Last updated: {notice.lastUpdated} · v{notice.version}
          </footer>
        </article>
      </div>
    </Modal>
  );
}

function BlockRenderer({ block }: { block: PrivacyNoticeBlock }) {
  switch (block.kind) {
    case 'heading':
      if (block.level === 2) {
        return (
          <h2 className="font-sans text-xl font-bold text-brand-dark mt-6 mb-2">
            {block.text}
          </h2>
        );
      }
      return (
        <h3 className="font-sans text-lg font-semibold text-grayscale-600 mt-4 mb-2">
          {block.text}
        </h3>
      );
    case 'paragraph':
      return (
        <p className="font-sans text-base leading-relaxed text-grayscale-600 mb-3">
          {linkifyEmails(block.text)}
        </p>
      );
    case 'bullets':
      return (
        <ul className="list-disc pl-6 mb-3 space-y-1.5 font-sans text-base leading-relaxed text-grayscale-600">
          {block.items.map((item, i) => (
            <li key={i}>{linkifyEmails(item)}</li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <div className="my-4 overflow-x-auto rounded-lg border border-grayscale-200">
          <table className="w-full border-collapse text-left font-sans text-sm">
            <thead className="bg-purple-100 text-grayscale-600">
              <tr>
                {block.headers.map((h, i) => (
                  <th
                    key={i}
                    scope="col"
                    className="px-4 py-2.5 font-semibold border-b border-grayscale-200"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="text-grayscale-600">
              {block.rows.map((row, r) => (
                <tr key={r} className="even:bg-grayscale-100/40">
                  {row.map((cell, c) => (
                    <td
                      key={c}
                      className="px-4 py-2.5 align-top border-b border-grayscale-200 last:border-b-0"
                    >
                      {linkifyEmails(cell)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
  }
}

// Turn `enquiries@norhi.io` → mailto link without ever altering the
// supplied legal copy. Intentionally narrow: only matches that exact address
// (the only email mentioned in either notice). Anything else passes through.
function linkifyEmails(text: string): React.ReactNode {
  const email = 'enquiries@norhi.io';
  if (!text.includes(email)) return text;
  const parts = text.split(email);
  const out: React.ReactNode[] = [];
  parts.forEach((part, i) => {
    out.push(part);
    if (i < parts.length - 1) {
      out.push(
        <a
          key={`mail-${i}`}
          href={`mailto:${email}`}
          className="text-brand-purple underline hover:text-brand-purple-hover"
        >
          {email}
        </a>,
      );
    }
  });
  return <>{out}</>;
}
